import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
}

interface Family {
  id: string;
  name: string;
}

interface Member {
  id: string;
  name?: string;
  ageGroup: string;
  avatarColor?: string;
  is_parent?: number | boolean;
}

interface AuthContextType {
  user: User | null;
  family: Family | null;
  member: Member | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string, familyName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const data = await api.getMe();
      setUser(data.user);
      setFamily(data.family);
      setMember(data.member);
    } catch (error) {
      setUser(null);
      setFamily(null);
      setMember(null);
    }
  };

  // Refresh auth - handles both user login and magic link login
  const refreshAuth = async () => {
    const token = api.getToken();
    if (!token) return;

    try {
      const data = await api.getMe();
      setUser(data.user || null);
      setFamily(data.family);
      setMember(data.member);
    } catch (error) {
      // If getMe fails, clear everything
      setUser(null);
      setFamily(null);
      setMember(null);
    }
  };

  useEffect(() => {
    const token = api.getToken();
    if (token) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const data = await api.login(email, password);
    setUser(data.user);
    setFamily(data.family);
    setMember(data.member);
  };

  const signup = async (email: string, password: string, name: string, familyName?: string) => {
    const data = await api.signup(email, password, name, familyName);
    setUser(data.user);
    setFamily(data.family);
    setMember(data.member);
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setFamily(null);
    setMember(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        family,
        member,
        isAuthenticated: !!(user || member),
        isLoading,
        login,
        signup,
        logout,
        refreshUser,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
